import { z } from 'zod';
import {
  AiTargetType,
  FeedbackKind,
  FeedbackTarget,
  RequestTargetType,
  RequestType,
  ReportType,
  RiskLevel,
  TaskPriority,
  TaskStudentStatus,
  Visibility,
} from '../enums';
import { isoDate, isoDateTime, nonEmptyText, optionalText, paginationSchema, urlSchema, uuidSchema, queryBoolean } from './common';

const e = <T extends Record<string, string>>(o: T) => z.enum(Object.values(o) as [T[keyof T], ...T[keyof T][]]);

// ---------------------------------------------------------------- Tarefas

export const createTaskSchema = z.object({
  title: nonEmptyText(200, 'Título'),
  description: optionalText(4000),
  doneCriteria: nonEmptyText(1000, 'Critério de pronto'),
  assigneeId: uuidSchema.nullable().optional(),
  dueDate: isoDate.nullable().optional(),
  priority: e(TaskPriority).default('MEDIUM'),
  riskLevel: e(RiskLevel).default('NORMAL'),
  checkpointId: uuidSchema.nullable().optional(),
  dependsOnIds: z.array(uuidSchema).max(20).default([]),
  publish: z.boolean().default(false),
});
export type CreateTaskInput = z.infer<typeof createTaskSchema>;

export const updateTaskSchema = z.object({
  title: nonEmptyText(200, 'Título').optional(),
  description: optionalText(4000),
  doneCriteria: nonEmptyText(1000, 'Critério de pronto').optional(),
  assigneeId: uuidSchema.nullable().optional(),
  dueDate: isoDate.nullable().optional(),
  priority: e(TaskPriority).optional(),
  riskLevel: e(RiskLevel).optional(),
  checkpointId: uuidSchema.nullable().optional(),
  dependsOnIds: z.array(uuidSchema).max(20).optional(),
});
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

export const publishTasksSchema = z.object({ taskIds: z.array(uuidSchema).min(1).max(50) });

export const evidenceInputSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('FILE'), fileId: uuidSchema, note: optionalText(500) }),
  z.object({ type: z.literal('LINK'), url: urlSchema, note: optionalText(500) }),
  z.object({ type: z.literal('COMMIT'), url: urlSchema, note: optionalText(500) }),
]);
export type EvidenceInput = z.infer<typeof evidenceInputSchema>;

export const declareTaskSchema = z.object({
  studentStatus: e(TaskStudentStatus),
  note: optionalText(2000),
  evidence: z.array(evidenceInputSchema).max(10).default([]),
});
export type DeclareTaskInput = z.infer<typeof declareTaskSchema>;

export const validateTaskSchema = z.object({
  decision: z.enum(['CONFIRM', 'REQUEST_CHANGES']),
  note: optionalText(2000),
});
export type ValidateTaskInput = z.infer<typeof validateTaskSchema>;

export const listTasksQuerySchema = z.object({
  status: z.enum(['open', 'review', 'done', 'draft', 'all']).default('all'),
});

// ---------------------------------------------------------------- Entregas

export const createDeliverableSchema = z.object({
  title: nonEmptyText(200, 'Título'),
  description: optionalText(4000),
  checkpointId: uuidSchema.nullable().optional(),
  dueDate: isoDate.nullable().optional(),
});
export type CreateDeliverableInput = z.infer<typeof createDeliverableSchema>;

export const submitVersionSchema = z
  .object({
    note: optionalText(4000),
    links: z.array(z.object({ url: urlSchema, label: optionalText(200) })).max(10).default([]),
    fileIds: z.array(uuidSchema).max(20).default([]),
  })
  .refine((v) => v.links.length + v.fileIds.length > 0 || Boolean(v.note), {
    message: 'Envie ao menos um arquivo, link ou descrição',
    path: ['fileIds'],
  });
export type SubmitVersionInput = z.infer<typeof submitVersionSchema>;

export const reviewVersionSchema = z.object({
  decision: z.enum(['APPROVE', 'REQUEST_CHANGES']),
  comment: z.string().trim().max(8000).optional(),
});
export type ReviewVersionInput = z.infer<typeof reviewVersionSchema>;

// ---------------------------------------------------------------- Feedback

export const createFeedbackSchema = z.object({
  targetType: e(FeedbackTarget),
  targetId: uuidSchema,
  body: nonEmptyText(8000, 'Comentário'),
  kind: e(FeedbackKind).default('COMMENT'),
  visibility: e(Visibility).default('ALL_PARTICIPANTS'),
});
export type CreateFeedbackInput = z.infer<typeof createFeedbackSchema>;

// ---------------------------------------------------------------- Check-ins

export const NOT_DELIVERED_REASONS = {
  UNDERESTIMATED: 'Subestimei o esforço',
  WAITING_ADVISOR: 'Aguardando decisão/retorno da orientação',
  DATA_ACCESS: 'Acesso a dados',
  INFRASTRUCTURE: 'Infraestrutura/equipamento',
  HEALTH_PERSONAL: 'Saúde/questões pessoais',
  OTHER_COMMITMENTS: 'Outras obrigações acadêmicas/profissionais',
  OTHER: 'Outro',
} as const;
export type NotDeliveredReason = keyof typeof NOT_DELIVERED_REASONS;

export const BLOCKER_TYPES = {
  ADVISOR_DECISION: 'Preciso de decisão da orientação',
  DATA_ACCESS: 'Acesso a dados',
  INFRASTRUCTURE: 'Infraestrutura',
  OTHER: 'Outro',
} as const;
export type BlockerType = keyof typeof BLOCKER_TYPES;

/** As 6 perguntas do check-in (proposta §4). */
export const checkInAnswersSchema = z.object({
  delivered: z
    .array(
      z.object({
        description: nonEmptyText(1000, 'Descrição'),
        fileIds: z.array(uuidSchema).max(10).default([]),
        links: z.array(urlSchema).max(10).default([]),
      }),
    )
    .max(20)
    .default([]),
  tasks: z
    .array(z.object({ taskId: uuidSchema, status: e(TaskStudentStatus), note: optionalText(500) }))
    .max(50)
    .default([]),
  notDelivered: z
    .array(
      z.object({
        description: nonEmptyText(1000, 'Descrição'),
        reason: z.enum(Object.keys(NOT_DELIVERED_REASONS) as [NotDeliveredReason, ...NotDeliveredReason[]]),
        details: optionalText(1000),
      }),
    )
    .max(20)
    .default([]),
  nextPromises: z
    .array(z.object({ description: nonEmptyText(500, 'Descrição'), dueDate: isoDate }))
    .min(1, 'Informe ao menos uma entrega prevista até o próximo check-in')
    .max(10),
  blockers: z
    .array(
      z.object({
        type: z.enum(Object.keys(BLOCKER_TYPES) as [BlockerType, ...BlockerType[]]),
        description: nonEmptyText(1000, 'Descrição'),
      }),
    )
    .max(10)
    .default([]),
  /** Pergunta 6: proposta do aluno ANTES de ver sugestões do sistema. */
  proposedNextStep: nonEmptyText(2000, 'Próximo passo proposto'),
  /** Texto livre (digitado ou colado) explicando o andamento. */
  freeText: optionalText(20000),
});
export type CheckInAnswers = z.infer<typeof checkInAnswersSchema>;

export const submitCheckInSchema = z.object({ answers: checkInAnswersSchema });
export type SubmitCheckInInput = z.infer<typeof submitCheckInSchema>;

export const reviewCheckInSchema = z.object({ note: optionalText(4000) });

// ---------------------------------------------------------------- Reuniões

export const createMeetingSchema = z.object({
  scheduledAt: isoDateTime,
  durationMinutes: z.number().int().min(10).max(600).default(60),
  location: optionalText(500),
  agenda: optionalText(8000),
});
export type CreateMeetingInput = z.infer<typeof createMeetingSchema>;

export const updateMeetingSchema = z.object({
  scheduledAt: isoDateTime.optional(),
  durationMinutes: z.number().int().min(10).max(600).optional(),
  location: optionalText(500),
  agenda: optionalText(8000),
  notes: z.string().max(20000).optional(),
  minutes: z.string().max(20000).optional(),
  decisions: z
    .array(z.object({ text: nonEmptyText(1000, 'Decisão'), advisorDecision: z.boolean().default(false) }))
    .max(30)
    .optional(),
  status: z.enum(['SCHEDULED', 'HELD', 'CANCELLED']).optional(),
});
export type UpdateMeetingInput = z.infer<typeof updateMeetingSchema>;

export const actionItemSchema = z.object({
  description: nonEmptyText(500, 'Encaminhamento'),
  assigneeId: uuidSchema.nullable().optional(),
  dueDate: isoDate.nullable().optional(),
  doneCriteria: optionalText(1000),
});
export type ActionItemInput = z.infer<typeof actionItemSchema>;

export const convertActionItemsSchema = z.object({ actionItemIds: z.array(uuidSchema).min(1).max(30) });

// ---------------------------------------------------------------- Solicitações

export const createRequestSchema = z
  .object({
    type: e(RequestType),
    targetType: e(RequestTargetType),
    targetId: uuidSchema.optional(),
    proposedDate: isoDate.optional(),
    justification: nonEmptyText(4000, 'Justificativa'),
  })
  .refine((v) => v.type !== 'RENEGOTIATE_DEADLINE' || Boolean(v.proposedDate), {
    message: 'Informe a data proposta',
    path: ['proposedDate'],
  })
  .refine((v) => v.targetType === 'WORK' || Boolean(v.targetId), { message: 'Informe o item', path: ['targetId'] });
export type CreateRequestInput = z.infer<typeof createRequestSchema>;

export const decideRequestSchema = z.object({
  decision: z.enum(['ACCEPT', 'REJECT']),
  note: nonEmptyText(2000, 'Resposta'),
  /** Ao aceitar renegociação: cria prorrogação formal com esta data (passa pelo fluxo de aprovação). */
  approvedDueDate: isoDate.optional(),
});
export type DecideRequestInput = z.infer<typeof decideRequestSchema>;

// ---------------------------------------------------------------- Relatórios / IA / Busca

export const createReportSchema = z
  .object({
    type: e(ReportType),
    workId: uuidSchema.optional(),
    institutionId: uuidSchema.optional(),
  })
  .refine((v) => v.type !== 'WORK_SHEET_PDF' || Boolean(v.workId), { message: 'Informe o trabalho', path: ['workId'] });
export type CreateReportInput = z.infer<typeof createReportSchema>;

export const requestAiAnalysisSchema = z.object({ targetType: e(AiTargetType), targetId: uuidSchema });
export type RequestAiAnalysisInput = z.infer<typeof requestAiAnalysisSchema>;

export const reviewSuggestionSchema = z.object({
  decision: z.enum(['ACCEPT', 'EDIT', 'REJECT']),
  editedText: z.string().trim().max(8000).optional(),
  /** Para sugestões de tarefa: edição dos campos. */
  editedTask: z
    .object({
      title: nonEmptyText(200, 'Título'),
      doneCriteria: nonEmptyText(1000, 'Critério de pronto'),
      dueDate: isoDate.nullable().optional(),
    })
    .optional(),
});
export type ReviewSuggestionInput = z.infer<typeof reviewSuggestionSchema>;

export const searchQuerySchema = z.object({ q: z.string().trim().min(2).max(120) });

export const listNotificationsQuerySchema = paginationSchema.extend({ unread: queryBoolean.default(false) });

export const calendarQuerySchema = z.object({ from: isoDate, to: isoDate, institutionId: uuidSchema.optional() });
